import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Popover, Select, Spin, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode } from "@fortawesome/free-solid-svg-icons";
import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import debounce from "lodash.debounce";
import { getAllTaskGroupQuery } from "../../util/query/taskgroup.query";
import { getSitesQuery } from "@src/util/query/site.query";
import { LEGACY_GROUP_TENANT_ID } from "@src/util/constants/tenant.constants";


export const BasicInfo = ({basicInfo, setBasicInfo, setWidth, selectedObjectType, visible, selectedCustomer}) =>{

    const tenantId = useSelector(state => state?.userAuthReducer?.authenticatedUserDetail?.tenantId);
    const isLegacyTenant = tenantId === LEGACY_GROUP_TENANT_ID;

    const { loading:groupLoading, data:groupList , refetch:groupRefetch } = useQuery(getAllTaskGroupQuery,{
        fetchPolicy: 'cache-and-network',
        skip: !isLegacyTenant,
    });

    // selected sites are tracked separately so their labels survive once the search term changes / clears
    const [selectedSites, setSelectedSites] = useState(basicInfo?.siteDetails?.map((site)=>({value: site?._id, label: site?.sitename})) || []);

    // siteSearchInput updates immediately as the user types; siteSearch (debounced) drives the actual query.
    // Keeping both lets us tell "still waiting for debounce" apart from "query ran and found nothing".
    const [siteSearchInput, setSiteSearchInput] = useState("");
    const [siteSearch, setSiteSearch] = useState("");
    const { loading:siteLoading, data:siteData } = useQuery(getSitesQuery,{
        fetchPolicy: 'cache-and-network',
        skip: isLegacyTenant || !siteSearch,
        variables: {
            input: {
                filters: null,
                pageSize: 20,
                page: 1,
                search: siteSearch
            }
        }
    });
    const debouncedSiteSearch = useMemo(()=>debounce((value)=>setSiteSearch(value), 400), []);
    const searchPending = siteSearchInput !== siteSearch;

    // keep the selected-site labels in sync once an edited task's site details load in
    useEffect(()=>{
        if(basicInfo?.siteDetails?.length>0){
            setSelectedSites(basicInfo.siteDetails.map((site)=>({value: site?._id, label: site?.sitename})));
        }
    },[basicInfo?.siteDetails]);

    const siteOptions = useMemo(()=>{
        const searchedOptions = siteData?.sites?.sites?.map((site)=>({value: site?._id, label: site?.sitename})) || [];
        const merged = [...selectedSites];
        searchedOptions.forEach((option)=>{
            if(!merged.find((s)=>s.value===option.value)){
                merged.push(option);
            }
        });
        return merged;
    },[siteData, selectedSites]);

    useEffect(()=>{
        if(isLegacyTenant){
            groupRefetch();
        }
        setBasicInfo({...basicInfo, objectType: selectedObjectType})

    },[visible]);

    useEffect(()=>{
        setBasicInfo({...basicInfo, objectType: selectedObjectType})
    },[selectedObjectType]);

    useEffect(()=>{
        if(isLegacyTenant && groupLoading){
            if(basicInfo?.groupId){
                const {groupId, groupName, ...rest} = basicInfo;
                setBasicInfo({...rest});
            }
        }
    },[groupLoading]);



    return(
        <div className="general-form">

            {isLegacyTenant ?
            <Form.Item>
                <label>Group <sup>*</sup></label>
                <Select
                    showSearch
                    optionFilterProp="children"
                    className="custom-select"
                    labelInValue
                    placeholder="Select a group"
                    onChange={(e)=>{
                        if(!groupLoading){

                            setBasicInfo({...basicInfo, groupId:e.value, groupName: e.label});
                        }

                    }}
                    value={groupLoading ? null : basicInfo?.groupName}
                    suffixIcon={groupLoading ? <Spin indicator={<LoadingOutlined />} />: <span className="dropdowncaret"></span>}
                >
                    {!groupLoading ? groupList?.getAllTaskLibraryGroup?.response?.map((group, index)=>(
                        <Select.Option value={group._id} key={index}>{group.name}</Select.Option>
                    )):
                        <Select.Option hidden disabled>Loading ...</Select.Option>
                    }
                </Select>
            </Form.Item>
            :
            <Form.Item>
                <label>Sites <sup>*</sup></label>
                <Select
                    mode="multiple"
                    showSearch
                    filterOption={false}
                    className="custom-select"
                    placeholder="Search sites..."
                    maxTagCount={0}
                    maxTagPlaceholder={(omittedSites)=>`${omittedSites.length} site${omittedSites.length>1 ? 's' : ''} selected`}
                    onSearch={(value)=>{
                        setSiteSearchInput(value);
                        debouncedSiteSearch(value);
                    }}
                    onChange={(siteIds, options)=>{
                        setBasicInfo({...basicInfo, siteIds});
                        setSelectedSites(options?.map((option)=>({value: option.value, label: option.label})));
                    }}
                    value={basicInfo?.siteIds || []}
                    options={siteOptions}
                    loading={siteLoading || searchPending}
                    notFoundContent={!siteSearchInput ? "Type to search sites" : (siteLoading || searchPending) ? "Searching..." : "No sites found"}
                    suffixIcon={siteLoading ? <Spin indicator={<LoadingOutlined />} />: <span className="dropdowncaret"></span>}
                />
                {selectedSites?.length>0 &&
                    <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'8px'}}>
                        {selectedSites.map((site)=>(
                            <Tag
                                key={site.value}
                                closable
                                onClose={()=>{
                                    const updatedSiteIds = (basicInfo?.siteIds||[])?.filter((id)=>id!==site.value);
                                    setBasicInfo({...basicInfo, siteIds: updatedSiteIds});
                                    setSelectedSites(selectedSites?.filter((s)=>s.value!==site.value));
                                }}
                            >
                                {site.label}
                            </Tag>
                        ))}
                    </div>
                }
            </Form.Item>
            }

            <Form.Item>
                <label>Label <sup>*</sup></label>
                <div style={{display:'flex', columnGap:'16px'}}>
                    <Input className="generic-input-control"  
                    value={basicInfo?.label}
                    onChange={(e)=>setBasicInfo({...basicInfo, label:e.target.value})} />
                    <Popover 
                          overlayClassName="custom-popover"
                          content={<div>If you are using this property <br/> for an integration, you can <br/> access its internal name here.</div>} 
                          placement='left'
                        >
                        <FontAwesomeIcon icon={faCode}
                            style={{    
                                marginTop: '13px',
                                fontSize: '20px',
                                color: '#0091ae',
                                cursor: 'pointer',
                            }}
                        />
                    </Popover>
                </div>
            </Form.Item>

            <Form.Item>
                <label>Description</label>
                <Input className="generic-input-control" 
                  value={basicInfo?.description}
                  onChange={(e)=>setBasicInfo({...basicInfo, description:e.target.value})} 
                />

            </Form.Item>

        </div>
    );
}